import { z } from "zod";

import { api } from "@/redux/api";

import { ProductResponse } from "../../pages/Subscription/schema/product";
import {
	CreateStripeCheckoutSession,
	CreateStripeCheckoutSessionResponse,
	FinalizeStripeCheckoutSession,
	FinalizeStripeCheckoutSessionResponse,
	FinalizeStripeCheckoutSessionResponseSchema,
} from "../../pages/Subscription/schema/stripe-checkout-session";
import { StripeProductResponse } from "../../pages/Subscription/schema/stripe-product";

export interface Invoice {
	id: string;
	created: number;
	amount_paid: number;
	currency: string;
	status: string;
	invoice_pdf: string;
}

export interface InvoicesResponse {
	data: Invoice[];
	has_more: boolean;
}

export interface CardPaymentMethod {
	type: "card";
	brand: string | null;
	last4: string | null;
	expMonth: number | null;
	expYear: number | null;
	billingEmail: string | null;
}

export interface SepaDebitPaymentMethod {
	type: "sepa_debit";
	last4: string | null;
	billingEmail: string | null;
	sepaDebit: {
		country: string | null;
		bankCode: string | null;
	};
}

export type PaymentMethod = CardPaymentMethod | SepaDebitPaymentMethod;

export interface BuyCreditsRequest {
	credits: number;
}

export interface BuyCreditsResponse {
	sessionId: string;
	sessionUrl: string;
}

export const PreviewBuyCreditsResponseSchema = z.object({
	credits: z.number(),
	pricePerCredit: z.number(),
	netAmount: z.number(),
	taxAmount: z.number(),
	totalAmount: z.number(),
	taxPercentage: z.number().nullable(),
	currency: z.string(),
});

export type PreviewBuyCreditsResponse = z.infer<typeof PreviewBuyCreditsResponseSchema>;

export interface PriceInfo {
	priceId: string;
	unitAmount: number;
	currency: string;
	formattedPrice: string;
}

export interface SubscriptionPricing {
	hasActiveSubscription: boolean;
	isAnnual: boolean;
	interval?: "year" | "month";
	emailPrice: PriceInfo | null;
	linkedinPrice: PriceInfo | null;
}

export interface ChangeLinkedinAmountRequest {
	amount: number;
	companyId?: number;
}

export interface ChangeLinkedinAmountResponse {
	success: boolean;
	message?: string;
	immediate?: boolean;
	pendingAmount?: number;
	pendingEffectiveAt?: string;
}

export interface ChangeEmailAmountResponse {
	success: boolean;
	message?: string;
	immediate?: boolean;
	pendingAmount?: number;
	pendingEffectiveAt?: string;
}

// Subscription change types
export interface SubscriptionInfo {
	productId: string;
	productName: string;
	credits: number;
	interval: "month" | "quarter" | "half_year" | "year";
	priceAmount: number;
	priceId: string;
}

export interface PreviewSubscriptionChangeRequest {
	priceId: string;
}

export interface PreviewSubscriptionChangeResponse {
	currentSubscription: SubscriptionInfo;
	newSubscription: SubscriptionInfo;
	changeType: "upgrade" | "downgrade" | "interval_change" | "no_change";
	immediate: boolean;
	proratedAmountPreview: number | null;
	effectiveAt: string | null;
	allowed: boolean;
	notAllowedReason?: string;
}

export interface ChangeSubscriptionRequest {
	priceId: string;
}

export interface ChangeSubscriptionResponse {
	success: boolean;
	immediate: boolean;
	effectiveAt?: string;
	message: string;
}

export interface PreviewSeatChangeRequest {
	type: "linkedin" | "email";
	amount: number;
}

export interface PreviewSeatChangeResponse {
	type: "linkedin" | "email";
	currentAmount: number;
	newAmount: number;
	bonusSeats: number;
	changeType: "increase" | "decrease" | "no_change";
	immediate: boolean;
	effectiveAt: string | null;
	unitPrice: number;
	currency: string;
	proratedAmountPreview: number | null;
	interval: "month" | "year";
}

export type SeatAddonType = "linkedin" | "email";

/** Body of `PATCH stripe-subscription/bonus-seats` — staff-only, see PREC-4063. */
export interface SetBonusSeatsRequest {
	companyId: number;
	type: SeatAddonType;
	/** Replaces the current bonus; it is not added to it. */
	bonusSeats: number;
}

export const SetBonusSeatsResponseSchema = z.object({
	success: z.boolean(),
	bonusSeats: z.number(),
});

export type SetBonusSeatsResponse = z.infer<typeof SetBonusSeatsResponseSchema>;

export const subscriptionApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getProducts: builder.query<ProductResponse, void>({
			query: () => ({
				url: "subscription/products",
			}),
			providesTags: ["Subscription"],
		}),
		getStripeProducts: builder.query<StripeProductResponse, void>({
			query: () => ({
				url: "stripe-subscription/products",
			}),
		}),
		createCheckoutSession: builder.mutation<
			CreateStripeCheckoutSessionResponse,
			CreateStripeCheckoutSession
		>({
			query: (body) => ({
				url: "stripe-subscription/create-checkout-session",
				method: "POST",
				body,
			}),
		}),
		createPortalSession: builder.query<CreateStripeCheckoutSessionResponse, void>({
			query: () => ({
				url: "stripe-subscription/create-portal-session",
			}),
		}),
		finalizeCheckoutSession: builder.mutation<
			FinalizeStripeCheckoutSessionResponse,
			FinalizeStripeCheckoutSession
		>({
			query: (body) => ({
				url: "stripe-subscription/finalize-checkout-session",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Me", "Credits"],
		}),
		skipTrial: builder.mutation<void, void>({
			query: () => ({
				url: "stripe-subscription/skip-trial",
				method: "POST",
				body: {},
			}),
			invalidatesTags: ["Credits"],
		}),
		cancelSubscription: builder.mutation<void, { subscriptionId: string }>({
			query: ({ subscriptionId }) => ({
				url: "stripe-subscription/cancel-subscription",
				method: "POST",
				body: { subscriptionId },
			}),
			invalidatesTags: ["Credits", "Me"],
		}),
		changeEmailAmount: builder.mutation<
			ChangeEmailAmountResponse,
			{ amount: number; companyId?: number }
		>({
			query: (body) => ({
				url: "stripe-subscription/change-email-amount",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Slots"],
		}),
		getPaymentMethod: builder.query<PaymentMethod, void>({
			query: () => ({
				url: "stripe-subscription/payment-method",
			}),
		}),
		buyCredits: builder.mutation<BuyCreditsResponse, BuyCreditsRequest>({
			query: (body) => ({
				url: "stripe-subscription/buy-credits",
				method: "POST",
				body,
			}),
		}),
		previewBuyCredits: builder.mutation<PreviewBuyCreditsResponse, BuyCreditsRequest>({
			query: (body) => ({
				url: "stripe-subscription/preview-buy-credits",
				method: "POST",
				body,
				responseSchema: PreviewBuyCreditsResponseSchema,
			}),
		}),
		getPricing: builder.query<SubscriptionPricing, void>({
			query: () => ({
				url: "stripe-subscription/pricing",
			}),
		}),
		changeLinkedinAmount: builder.mutation<
			ChangeLinkedinAmountResponse,
			ChangeLinkedinAmountRequest
		>({
			query: (body) => ({
				url: "stripe-subscription/change-linkedin-amount",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Slots"],
		}),
		previewSubscriptionChange: builder.mutation<
			PreviewSubscriptionChangeResponse,
			PreviewSubscriptionChangeRequest
		>({
			query: (body) => ({
				url: "stripe-subscription/preview-change",
				method: "POST",
				body,
			}),
		}),
		changeSubscription: builder.mutation<ChangeSubscriptionResponse, ChangeSubscriptionRequest>({
			query: (body) => ({
				url: "stripe-subscription/change",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Credits", "Me"],
		}),
		previewSeatChange: builder.mutation<PreviewSeatChangeResponse, PreviewSeatChangeRequest>({
			query: (body) => ({
				url: "stripe-subscription/preview-seat-change",
				method: "POST",
				body,
			}),
		}),
		finalizeCreditsPurchase: builder.mutation<
			FinalizeStripeCheckoutSessionResponse,
			FinalizeStripeCheckoutSession
		>({
			query: (body) => ({
				url: "stripe-subscription/finalize-credits-purchase",
				method: "POST",
				body,
				responseSchema: FinalizeStripeCheckoutSessionResponseSchema,
			}),
			invalidatesTags: ["Credits"],
		}),
		setBonusSeats: builder.mutation<SetBonusSeatsResponse, SetBonusSeatsRequest>({
			query: (body) => ({
				url: "stripe-subscription/bonus-seats",
				method: "PATCH",
				body,
				responseSchema: SetBonusSeatsResponseSchema,
			}),
			// The admin table row (`Companies`) and the per-company seat cards (`Slots`) both show
			// the new capacity without a reload.
			invalidatesTags: ["Slots", "Companies"],
		}),
		reactivateSubscription: builder.mutation<{ success: boolean }, { subscriptionId: string }>({
			query: ({ subscriptionId }) => ({
				url: "stripe-subscription/reactivate-subscription",
				method: "POST",
				body: { subscriptionId },
			}),
			invalidatesTags: ["Credits", "Me"],
		}),
	}),
});

export const {
	useGetProductsQuery,
	useGetStripeProductsQuery,
	useCreateCheckoutSessionMutation,
	useFinalizeCheckoutSessionMutation,
	useLazyCreatePortalSessionQuery,
	useSkipTrialMutation,
	useCancelSubscriptionMutation,
	useChangeEmailAmountMutation,
	useGetPaymentMethodQuery,
	useBuyCreditsMutation,
	usePreviewBuyCreditsMutation,
	useGetPricingQuery,
	useChangeLinkedinAmountMutation,
	usePreviewSubscriptionChangeMutation,
	useChangeSubscriptionMutation,
	usePreviewSeatChangeMutation,
	useFinalizeCreditsPurchaseMutation,
	useSetBonusSeatsMutation,
	useReactivateSubscriptionMutation,
} = subscriptionApi;
export const accountApiReducer = subscriptionApi.reducer;
export const accountApiMiddleware = subscriptionApi.middleware;
